// Bank connection profiles console (admin-portal) — the PLATFORM surface for a
// tenant's per-bank SFTP host-to-host endpoints (PayGate D18). Non-secret settings
// are returned; SSH keys / passphrase / host key / PGP key are write-only (sealed
// server-side, never returned — only has_credentials). All reached through the
// gateway at /api/v1/paygate/admin/connection-profiles/* (platform-role-guarded).
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const BASE = `${API_V1}/paygate/admin/connection-profiles`;

export interface Profile {
  id: string;
  tenant_id: string;
  country: string;
  bank_code: string;
  connection_type: string; // api | gateway | sftp | manual
  environment: string; // sandbox | live
  enabled: boolean;
  priority: number;
  host: string;
  port: number;
  username: string;
  remote_upload_dir: string;
  remote_ack_dir: string;
  remote_return_dir: string;
  file_naming_pattern: string;
  file_format: string; // csv | xlsx | iso20022
  pgp_required: boolean;
  manual_fallback_enabled: boolean;
  poll_interval_seconds: number;
  timeout_seconds: number;
  has_credentials: boolean;
  created_at: string;
  updated_at: string;
}

export interface Secrets {
  password?: string;
  private_key?: string;
  passphrase?: string;
  host_key?: string;
  insecure_ignore_host_key?: boolean;
  pgp_public_key?: string;
}

export type ProfileInput = Partial<Profile> & { secrets?: Secrets | null };

export const listProfiles = (tenantId: string) =>
  axios.get<{ profiles: Profile[] }>(`${BASE}?tenantId=${tenantId}`).then((r) => r.data.profiles ?? []);

export const saveProfile = (tenantId: string, p: ProfileInput, id?: string) =>
  (id
    ? axios.put<Profile>(`${BASE}/${id}?tenantId=${tenantId}`, p)
    : axios.post<Profile>(`${BASE}?tenantId=${tenantId}`, p)
  ).then((r) => r.data);

export const setProfileEnabled = (tenantId: string, id: string, enabled: boolean) =>
  axios.post<Profile>(`${BASE}/${id}/enabled?tenantId=${tenantId}`, { enabled }).then((r) => r.data);

export const deleteProfile = (tenantId: string, id: string) =>
  axios.delete(`${BASE}/${id}?tenantId=${tenantId}`).then((r) => r.data);

export const testProfile = (tenantId: string, id: string, secrets?: Secrets) =>
  axios
    .post<{ ok: boolean; error?: string; message?: string }>(`${BASE}/${id}/test?tenantId=${tenantId}`, { secrets })
    .then((r) => r.data);
